import React, { useState } from 'react';
import { styled } from '@mui/material/styles';
import {
    Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper,
    Typography, Box, Divider, Grid, Button, Dialog, DialogTitle, DialogContent, DialogActions, TextField, CircularProgress
} from "@mui/material";
import { toast } from 'react-toastify';
import axios from 'axios';
import { Payment } from '../../Svg';
import { fomateDate } from '../../utils/fomateDate';
import { dispatchScheduleorderThunk, setschedulerefresh } from '../../redux/schedulecartslice';
import { useDispatch } from 'react-redux';
import api from '../../utils/api';
import DeleteModal from '../reusable/deleteModal/DeleteModal';

// Uploads a file through the presigned-URL flow and returns its public URL
const uploadToS3 = async (file, folder) => {
    const body = { key: `${folder}/${Date.now()}_${file.name}`, fileType: file.type, fileName: file.name };
    const { data, status } = await api.getPutSignedUrl(body);
    if (status !== 200) throw new Error('Could not get an upload URL');
    await axios.put(data.data?.preSigned, file, { headers: { 'Content-Type': file.type } });
    return data?.data?.url;
};

// Small caption + input used in the dispatch dialogs
const Field = ({ label, hint, children }) => (
    <Box sx={{ flex: 1, minWidth: 180 }}>
        <Typography sx={{ fontSize: 12, color: '#667085', mb: 0.5 }}>{label}{hint && <span style={{ color: '#98A2B3' }}> ({hint})</span>}</Typography>
        {children}
    </Box>
);

// Statuses in which items can still be dispatched
const DISPATCHABLE = ['APPROVED', 'AWAITING', 'RESCHEDULE', 'PENDING', 'DISPATCHED'];
// Catalog rows are keyed by productId, parts not in the catalog by part number
const rowKey = (p) => p?.productId || `pn:${p?.productNumber || ''}`;
const partName = (p) => p?.productDetails?.productOverview?.productNumber?.[0] || p?.productNumber || 'Part';

const TableHeads = styled(TableCell)(() => ({
    fontSize: "14px",
    color: "#727A90 !important",
    fontFamily: `'Public Sans', sans-serif !important`,
}));

const ActionButton = styled(Button)(({ theme }) => ({
    textTransform: 'none',
    marginRight: theme.spacing(1),
    fontSize: '0.875rem',
    color: '#512DA8',
    fontSize: '14px',
    fontWeight: '600 !important',
    fontFamily: `'Public Sans', sans-serif`,
    '&:hover': {
        backgroundColor: theme.palette.grey[200],
    },
}));

const TableCells = styled(TableCell)(() => ({
    fontFamily: `'Public Sans', sans-serif !important`,
    fontSize: "14px",
    color: '#0C0D0F',
}));

const OrderDetailsProds = ({ products, price, id, status, dispatches = [] }) => {
    const dispatch = useDispatch()
    const [selectedProducts, setSelectedProducts] = useState([]);
    const [loading, setLoading] = useState(false);
    const [dialogOpen, setDialogOpen] = useState(false);
    const [dispatchQty, setDispatchQty] = useState({});
    const [invoiceNumber, setInvoiceNumber] = useState('');
    const [invoiceUri, setInvoiceUri] = useState('');
    const [invoiceName, setInvoiceName] = useState('');
    const [invoiceUploading, setInvoiceUploading] = useState(false);
    const handleInvoiceUpload = async (e) => {
        const file = e.target.files?.[0];
        e.target.value = '';
        if (!file) return;
        try {
            setInvoiceUploading(true);
            const url = await uploadToS3(file, 'invoices/uploaded');
            setInvoiceUri(url);
            setInvoiceName(file.name);
            toast.success('Invoice uploaded');
        } catch (err) {
            console.error(err);
            toast.error('Could not upload the invoice');
        } finally {
            setInvoiceUploading(false);
        }
    };
    // Editing / deleting a past dispatch
    const [editing, setEditing] = useState(null);
    const [editForm, setEditForm] = useState({});
    const [editSaving, setEditSaving] = useState(false);
    const [editUploading, setEditUploading] = useState(false);
    const [deleting, setDeleting] = useState(null);
    const [deleteLoading, setDeleteLoading] = useState(false);

    const openEdit = (d) => {
        setEditing(d);
        setEditForm({ invoiceNumber: d?.invoiceNumber || '', invoiceUri: d?.invoiceUri || '', invoiceName: '', courierName: d?.courierName || '', trackingNumber: d?.trackingNumber || '', remarks: d?.remarks || '' });
    };
    const handleEditUpload = async (e) => {
        const file = e.target.files?.[0];
        e.target.value = '';
        if (!file) return;
        try {
            setEditUploading(true);
            const url = await uploadToS3(file, 'invoices/uploaded');
            setEditForm((f) => ({ ...f, invoiceUri: url, invoiceName: file.name }));
            toast.success('Invoice uploaded');
        } catch (err) {
            console.error(err);
            toast.error('Could not upload the invoice');
        } finally {
            setEditUploading(false);
        }
    };
    const saveEdit = async () => {
        if (!editing) return;
        setEditSaving(true);
        try {
            await api.updateScheduleDispatch(id, editing._id, {
                invoiceNumber: editForm.invoiceNumber,
                invoiceUri: editForm.invoiceUri,
                courierName: editForm.courierName,
                trackingNumber: editForm.trackingNumber,
                remarks: editForm.remarks,
            });
            toast.success('Dispatch updated');
            dispatch(setschedulerefresh());
            setEditing(null);
        } catch (err) {
            toast.error(err?.response?.data?.message || 'Could not update the dispatch');
        } finally {
            setEditSaving(false);
        }
    };
    const confirmDelete = async () => {
        if (!deleting) return;
        setDeleteLoading(true);
        try {
            await api.deleteScheduleDispatch(id, deleting._id);
            toast.success('Dispatch removed, quantities are pending again');
            dispatch(setschedulerefresh());
            setDeleting(null);
        } catch (err) {
            toast.error(err?.response?.data?.message || 'Could not remove the dispatch');
        } finally {
            setDeleteLoading(false);
        }
    };

    const canDispatch = DISPATCHABLE.includes(status);

    // GST for the summary: stored tax, or derived from the lines for older schedules saved with tax 0
    const DEFAULT_GST = 18;
    const rateOf = (p) => (Number.isFinite(Number(p?.gstPercent)) ? Number(p.gstPercent) : DEFAULT_GST);
    const subTotalAmt = Number(price?.subTotal) || 0;
    const gstRates = [...new Set((products || []).map(rateOf))];
    // GST amount: stored tax, else the rate applied to the subtotal (older schedules were saved with tax 0)
    const gstRate = gstRates.length === 1 ? gstRates[0] : DEFAULT_GST;
    const gstAmt = (Number(price?.tax) || 0) > 0 ? Number(price.tax) : (subTotalAmt * gstRate) / 100;
    const grandTotal = subTotalAmt + gstAmt + (Number(price?.otherCharges) || 0);
    const dispatchableRows = (products || []).filter((p) => (p?.pendingOrderCount || 0) > 0);

    const handleCheckboxChange = (key) => {
        setSelectedProducts((prev) =>
            prev.includes(key)
                ? prev.filter(id => id !== key)
                : [...prev, key]
        );
    };
    const isProductSelected = (key) => selectedProducts?.includes(key);
    const allSelected = dispatchableRows.length > 0 && dispatchableRows.every((p) => isProductSelected(rowKey(p)));
    const toggleAll = () => setSelectedProducts(allSelected ? [] : dispatchableRows.map(rowKey));

    const selectedRows = (products || []).filter((p) => isProductSelected(rowKey(p)));
    // Default dispatch qty: the rescheduled qty if any, else the scheduled qty, never above pending
    const suggestedQty = (p) => Math.min(p?.pendingOrderCount || 0, (p?.rescheduled ? p?.rescheduledQuantity : p?.quantity) || p?.pendingOrderCount || 0);

    const openDialog = () => {
        const next = {};
        selectedRows.forEach((p) => { next[rowKey(p)] = suggestedQty(p); });
        setDispatchQty(next);
        setDialogOpen(true);
    };

    const invalidQty = selectedRows.filter((p) => {
        const q = Number(dispatchQty[rowKey(p)]);
        return !(q > 0) || q > (p?.pendingOrderCount || 0);
    });

    const handleDispatch = async () => {
        if (invalidQty.length) { toast.error(`Check the dispatch quantity for: ${invalidQty.map(partName).join(', ')}`); return; }
        const productUpdates = selectedRows.map((p) => ({
            productId: p?.productId || null,
            productNumber: p?.productNumber || partName(p),
            quantity: Number(dispatchQty[rowKey(p)]),
        }));
        setLoading(true);
        // Invoice number and PDF are generated by the server
        // Invoice number / PDF are generated by the server unless typed or uploaded here
        const result = await dispatch(dispatchScheduleorderThunk({ id, productUpdates, invoiceNumber: invoiceNumber.trim() || undefined, invoiceUri: invoiceUri || undefined }));
        setLoading(false);
        if (result?.payload === 200) {
            setDialogOpen(false);
            setSelectedProducts([]);
            setInvoiceNumber(''); setInvoiceUri(''); setInvoiceName('');
        }
    };

    const dispatchTotal = selectedRows.reduce((sum, p) => sum + (Number(dispatchQty[rowKey(p)]) || 0) * (p?.unitPrice || 0), 0);

    return (
        <div>

            <Dialog open={dialogOpen} onClose={() => !loading && setDialogOpen(false)} maxWidth="md" fullWidth>
                <DialogTitle sx={{ fontFamily: "'Public Sans', sans-serif", fontWeight: 700 }}>Dispatch {selectedRows.length} item(s)</DialogTitle>
                <DialogContent dividers>
                    <TableContainer>
                        <Table size="small" sx={{ '& td, & th': { verticalAlign: 'middle' }, '& tbody tr:last-of-type td': { borderBottom: 'none' } }}>
                            <TableHead>
                                <TableRow>
                                    <TableHeads>Product</TableHeads>
                                    <TableHeads align="right">Pending</TableHeads>
                                    <TableHeads align="right">Dispatch qty</TableHeads>
                                    <TableHeads align="right">Unit price</TableHeads>
                                    <TableHeads align="right">Amount</TableHeads>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {selectedRows.map((p) => {
                                    const key = rowKey(p);
                                    const q = Number(dispatchQty[key]) || 0;
                                    const bad = !(q > 0) || q > (p?.pendingOrderCount || 0);
                                    return (
                                        <TableRow key={key}>
                                            <TableCells>
                                                <strong>{partName(p)}</strong>
                                                <div style={{ fontSize: 12, color: '#667085' }}>{p?.productDetails?.productOverview?.manufacturer?.[0]?.name || p?.manufacturerName || ''}</div>
                                            </TableCells>
                                            <TableCells align="right">{p?.pendingOrderCount}</TableCells>
                                            <TableCells align="right" sx={{ verticalAlign: 'middle' }}>
                                                <TextField
                                                    size="small"
                                                    type="number"
                                                    value={dispatchQty[key] ?? ''}
                                                    onChange={(e) => setDispatchQty((prev) => ({ ...prev, [key]: e.target.value }))}
                                                    inputProps={{ min: 1, max: p?.pendingOrderCount || 0, style: { textAlign: 'right' } }}
                                                    error={bad}
                                                    title={bad ? `Enter 1 to ${p?.pendingOrderCount || 0}` : ''}
                                                    sx={{ width: 120, '& .MuiInputBase-root': { height: 40 } }}
                                                />
                                                {bad && <div style={{ fontSize: 11, color: '#d32f2f', marginTop: 2 }}>1 to {p?.pendingOrderCount || 0}</div>}
                                            </TableCells>
                                            <TableCells align="right">₹{(p?.unitPrice || 0).toFixed(2)}</TableCells>
                                            <TableCells align="right">₹{(q * (p?.unitPrice || 0)).toFixed(2)}</TableCells>
                                        </TableRow>
                                    );
                                })}
                                <TableRow>
                                    <TableCells colSpan={4} align="right" sx={{ fontWeight: 700 }}>Dispatch total</TableCells>
                                    <TableCells align="right" sx={{ fontWeight: 700 }}>₹{dispatchTotal.toFixed(2)}</TableCells>
                                </TableRow>
                            </TableBody>
                        </Table>
                    </TableContainer>
                    <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'flex-end', mt: 2 }}>
                        <Field label="Invoice number" hint="auto if blank">
                            <TextField size="small" fullWidth placeholder="e.g. EW-INV-1001" value={invoiceNumber} onChange={(e) => setInvoiceNumber(e.target.value)} />
                        </Field>
                        <Field label="Invoice file" hint="auto-generated if not uploaded">
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <Button component="label" variant="outlined" size="small" disabled={invoiceUploading} sx={{ textTransform: 'none', borderColor: '#512DA8', color: '#512DA8', height: 40, whiteSpace: 'nowrap' }}>
                                    {invoiceUploading ? 'Uploading…' : invoiceUri ? 'Replace invoice' : 'Upload invoice'}
                                    <input hidden type="file" accept=".pdf,.png,.jpg,.jpeg" onChange={handleInvoiceUpload} />
                                </Button>
                                {invoiceName && (
                                    <a href={invoiceUri} target="_blank" rel="noopener noreferrer" style={{ color: '#512DA8', fontSize: 13, overflowWrap: 'anywhere' }}>{invoiceName}</a>
                                )}
                            </Box>
                        </Field>
                    </Box>
                </DialogContent>
                <DialogActions sx={{ p: 2 }}>
                    <Button onClick={() => setDialogOpen(false)} disabled={loading} sx={{ textTransform: 'none', color: '#512DA8' }}>Cancel</Button>
                    <Button
                        variant="contained"
                        onClick={handleDispatch}
                        disabled={loading || invoiceUploading}
                        startIcon={loading ? <CircularProgress size={16} sx={{ color: '#fff' }} /> : null}
                        sx={{ textTransform: 'none', background: '#512DA8', '&:hover': { background: '#4527a0' } }}
                    >
                        {loading ? 'Dispatching…' : 'Confirm dispatch'}
                    </Button>
                </DialogActions>
            </Dialog>
            <Grid item xs={12} md={6} sx={{ p: '24px 0' }}>
                <Paper sx={{ boxShadow: '0px 4px 30px 0px #4D54640D', border: '1px solid #DCDEE3', borderRadius: "16px" }}>
                    <Box sx={{ p: 2 }} display="flex" alignItems="center">
                        <Payment />
                        <Typography style={{ marginLeft: '10px', fontSize: '18px', fontWeight: "600" }}>
                            Product
                        </Typography>
                        <Typography style={{ marginLeft: '10px', padding: "6px 4px", borderRadius: '8px', fontSize: '14px', fontWeight: "600", background: "#EAFFFE", color: '#006C64' }}>
                            {products?.length} Products
                        </Typography>
                        {canDispatch && dispatchableRows.length > 0 && (
                            <Box sx={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: 1.5 }}>
                                <Typography sx={{ fontSize: 13, color: '#727A90' }}>
                                    {selectedProducts.length > 0 ? `${selectedProducts.length} selected` : 'Tick items to dispatch'}
                                </Typography>
                                <ActionButton
                                    sx={{ background: selectedProducts.length ? '#512DA8' : '#E0E2E7', color: selectedProducts.length ? '#fff' : '#98A2B3', margin: 0, '&:hover': { background: '#4527a0' } }}
                                    onClick={openDialog}
                                    disabled={loading || selectedProducts.length === 0}
                                >
                                    Dispatch selected
                                </ActionButton>
                            </Box>
                        )}
                    </Box>
                    <Divider sx={{ mb: 1 }} />
                    <TableContainer>
                        <Table>
                            <TableHead>
                                <TableRow>
                                    <TableHeads sx={{ textAlign: 'left' }}>
                                        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                                            <span style={{ width: 18, display: 'inline-flex', justifyContent: 'center', flexShrink: 0 }}>
                                                {canDispatch && dispatchableRows.length > 0 && (
                                                    <input type="checkbox" checked={allSelected} onChange={toggleAll} title="Select all" style={{ margin: 0 }} />
                                                )}
                                            </span>
                                            Product
                                        </div>
                                    </TableHeads>
                                    <TableHeads>Total Quantity</TableHeads>
                                    <TableHeads>Scheduled Quantity</TableHeads>
                                    <TableHeads>Pending Quantity</TableHeads>
                                    <TableHeads>Price</TableHeads>
                                    <TableHeads>Schedule Date</TableHeads>
                                    <TableHeads>Total</TableHeads>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {products?.map((i) => (
                                    
                                    <>
                                        {i?.rescheduled != true &&
                                            <TableRow key={i._id}>
                                                <TableCells>
                                                    <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                                                        <span style={{ width: 18, display: 'inline-flex', justifyContent: 'center', flexShrink: 0 }}>
                                                            {canDispatch && (i?.pendingOrderCount || 0) > 0 &&
                                                                <input
                                                                    type="checkbox"
                                                                    checked={isProductSelected(rowKey(i))}
                                                                    onChange={() => handleCheckboxChange(rowKey(i))}
                                                                    style={{ margin: 0 }}
                                                                />
                                                            }
                                                        </span>
                                                        <div style={{ width: 44, height: 44, borderRadius: 6, border: '1px solid #E6E8EC', background: '#F9FAFB', display: 'flex', alignItems: 'center', justifyContent: 'center', overflow: 'hidden', flexShrink: 0 }}>
                                                            <img
                                                                src={i?.productDetails?.assets?.featuredImage?.[0] || "/assets/no-image-chip.svg"}
                                                                style={{ width: '100%', height: '100%', objectFit: 'contain', display: 'block' }}
                                                                alt="product"
                                                            />
                                                        </div>
                                                        <div>
                                                            <strong style={{ color: '#1D1F2C' }}>
                                                                {i?.productDetails?.productOverview?.productNumber?.[0] || i?.productNumber || <span style={{ color: '#B54708', fontSize: 12 }}>Product removed from catalog</span>}
                                                            </strong>
                                                            <div style={{ fontSize: '14px', color: '#667085', fontWeight: '500' }}>
                                                                {i?.productDetails?.productOverview?.manufacturer?.[0]?.name || i?.manufacturerName || '-'}
                                                            </div>
                                                        </div>
                                                    </div>
                                                </TableCells>
                                                <TableCells>{i?.totalQty}</TableCells>
                                                <TableCells>{i?.quantity}</TableCells>
                                                <TableCells>{i?.pendingOrderCount}</TableCells>
                                                <TableCells>₹{i?.unitPrice?.toFixed(2)}</TableCells>
                                                <TableCells>{fomateDate(i?.deliveryDate)}</TableCells>
                                                <TableCells>₹{(i?.quantity * i?.unitPrice)?.toFixed(2)}</TableCells>
                                            </TableRow>
                                        }
                                    </>
                                ))}

                                {products?.map((i) => (
                                    <>
                                        {i?.rescheduled == true &&
                                            <>
                                                <TableRow key={i._id}>
                                                    <TableCells style={{ background: '#F1F1F3', color: '#5D5FEF' }}>
                                                        <div > Reschedule details</div>
                                                    </TableCells>
                                                    <TableCells style={{ background: '#F1F1F3', color: '#5D5FEF' }}></TableCells>
                                                    <TableCells style={{ background: '#F1F1F3', color: '#5D5FEF' }}>
                                                        <div >Required qty :{i?.rescheduledQuantity}</div>
                                                    </TableCells>
                                                    <TableCells style={{ background: '#F1F1F3', color: '#5D5FEF' }}>
                                                        <div>Reschedule date : {fomateDate(i?.rescheduleDate)}</div>
                                                    </TableCells>
                                                    <TableCells style={{ background: '#F1F1F3', color: '#5D5FEF' }}></TableCells>
                                                    <TableCells style={{ background: '#F1F1F3', color: '#5D5FEF' }}></TableCells>
                                                    <TableCells style={{ background: '#F1F1F3', color: 'red' }}>
                                                        <div >On delivery</div>
                                                    </TableCells>
                                                </TableRow>
                                                <TableRow key={i._id}>
                                                    <TableCells>
                                                        <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                                                            <span style={{ width: 18, display: 'inline-flex', justifyContent: 'center', flexShrink: 0 }}>
                                                                {canDispatch && (i?.pendingOrderCount || 0) > 0 &&
                                                                    <input
                                                                        type="checkbox"
                                                                        checked={isProductSelected(rowKey(i))}
                                                                        onChange={() => handleCheckboxChange(rowKey(i))}
                                                                        style={{ margin: 0 }}
                                                                    />
                                                                }
                                                            </span>
                                                            <div style={{ width: 44, height: 44, borderRadius: 6, border: '1px solid #E6E8EC', background: '#F9FAFB', display: 'flex', alignItems: 'center', justifyContent: 'center', overflow: 'hidden', flexShrink: 0 }}>
                                                                <img
                                                                    src={i?.productDetails?.assets?.featuredImage?.[0] || "/assets/no-image-chip.svg"}
                                                                    style={{ width: '100%', height: '100%', objectFit: 'contain', display: 'block' }}
                                                                    alt="product"
                                                                />
                                                            </div>
                                                            <div>
                                                                <strong style={{ color: '#1D1F2C' }}>
                                                                    {i?.productDetails?.productOverview?.productNumber?.[0] || i?.productNumber || <span style={{ color: '#B54708', fontSize: 12 }}>Product removed from catalog</span>}
                                                                </strong>
                                                                <div style={{ fontSize: '14px', color: '#667085', fontWeight: '500' }}>
                                                                    {i?.productDetails?.productOverview?.manufacturer?.[0]?.name || i?.manufacturerName || '-'}
                                                                </div>
                                                            </div>
                                                        </div>
                                                    </TableCells>
                                                    <TableCells>{i?.totalQty}</TableCells>
                                                    <TableCells>{i?.quantity}</TableCells>
                                                    <TableCells>{i?.pendingOrderCount}</TableCells>
                                                    <TableCells>₹{i?.unitPrice?.toFixed(2)}</TableCells>
                                                    <TableCells>{fomateDate(i?.deliveryDate)}</TableCells>
                                                    <TableCells>₹{(i?.quantity * i?.unitPrice)?.toFixed(2)}</TableCells>
                                                </TableRow>
                                            </>
                                        }
                                    </>
                                ))}
                                {/* Subtotal Row */}
                                <TableRow>
                                    <TableCells colSpan={5}></TableCells>
                                    <TableCells>Subtotal</TableCells>
                                    <TableCells>₹{price?.subTotal?.toFixed(2)}</TableCells>
                                </TableRow>
                                <TableRow>
                                    <TableCells colSpan={5}></TableCells>
                                    <TableCells>GST ({gstRate}%)</TableCells>
                                    <TableCells>₹{gstAmt.toFixed(2)}</TableCells>
                                </TableRow>
                                <TableRow>
                                    <TableCells colSpan={5}></TableCells>
                                    <TableCells sx={{ borderBottom: 'none !important' }}>Shipping Rate</TableCells>
                                    <TableCells>₹{price?.otherCharges?.toFixed(2)}</TableCells>
                                </TableRow>
                            </TableBody>
                        </Table>
                    </TableContainer>
                </Paper>

                <Paper sx={{ padding: '20px 30px', mt: 2, boxShadow: '0px 4px 30px 0px #4D54640D', border: '1px solid #DCDEE3', borderRadius: "16px" }}>
                    <Box display="flex" justifyContent="space-between" fontWeight="bold">
                        <Typography variant="h6" sx={{ fontSize: '18px', fontWeight: 600 }}>Grand Total:</Typography>
                        <Typography variant="h6" sx={{ fontSize: '18px', fontWeight: 600 }}>₹{grandTotal.toFixed(2)}</Typography>
                    </Box>
                </Paper>

                {dispatches?.length > 0 && (
                    <Paper sx={{ mt: 2, boxShadow: '0px 4px 30px 0px #4D54640D', border: '1px solid #DCDEE3', borderRadius: "16px" }}>
                        <Box sx={{ p: 2 }} display="flex" alignItems="center" justifyContent="space-between">
                            <Typography style={{ fontSize: '18px', fontWeight: "600" }}>Dispatches & invoices</Typography>
                            <Typography sx={{ fontSize: 13, color: '#727A90' }}>{dispatches.length} dispatch(es)</Typography>
                        </Box>
                        <Divider />
                        <TableContainer>
                            <Table size="small">
                                <TableHead>
                                    <TableRow>
                                        <TableHeads>Date</TableHeads>
                                        <TableHeads>Invoice</TableHeads>
                                        <TableHeads>Items</TableHeads>
                                        <TableHeads>Courier</TableHeads>
                                        <TableHeads align="right">Amount</TableHeads>
                                        <TableHeads align="right">Actions</TableHeads>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {[...dispatches].reverse().map((d, idx) => (
                                        <TableRow key={d?._id || idx}>
                                            <TableCells>{fomateDate(d?.dispatchedAt)}</TableCells>
                                            <TableCells>
                                                <strong>{d?.invoiceNumber || '-'}</strong>
                                                {d?.invoiceUri && (
                                                    <a href={d.invoiceUri} target="_blank" rel="noopener noreferrer" style={{ marginLeft: 8, color: '#512DA8', fontSize: 13 }}>View invoice</a>
                                                )}
                                                {d?.remarks && <div style={{ fontSize: 12, color: '#667085' }}>{d.remarks}</div>}
                                            </TableCells>
                                            <TableCells>
                                                {(d?.items || []).map((it, j) => (
                                                    <div key={j} style={{ fontSize: 13 }}>{it?.productNumber || 'Part'} × {it?.quantity}</div>
                                                ))}
                                            </TableCells>
                                            <TableCells>
                                                {d?.courierName || d?.trackingNumber ? (
                                                    <>
                                                        <div style={{ fontSize: 13 }}>{d?.courierName || '-'}</div>
                                                        {d?.trackingNumber && <div style={{ fontSize: 12, color: '#667085' }}>AWB {d.trackingNumber}</div>}
                                                    </>
                                                ) : <span style={{ color: '#98A2B3', fontSize: 13 }}>Not added</span>}
                                            </TableCells>
                                            <TableCells align="right">₹{Number(d?.subTotal || 0).toFixed(2)}</TableCells>
                                            <TableCells align="right" sx={{ whiteSpace: 'nowrap' }}>
                                                <Button size="small" onClick={() => openEdit(d)} sx={{ textTransform: 'none', color: '#512DA8', minWidth: 0 }}>Edit</Button>
                                                <Button size="small" color="error" onClick={() => setDeleting(d)} sx={{ textTransform: 'none', minWidth: 0 }}>Delete</Button>
                                            </TableCells>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    </Paper>
                )}

                {/* Edit a past dispatch: invoice, courier, remarks */}
                <Dialog open={Boolean(editing)} onClose={() => !editSaving && setEditing(null)} maxWidth="sm" fullWidth>
                    <DialogTitle sx={{ fontFamily: "'Public Sans', sans-serif", fontWeight: 700 }}>Edit dispatch {editing?.invoiceNumber ? `· ${editing.invoiceNumber}` : ''}</DialogTitle>
                    <DialogContent dividers>
                        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                            <Field label="Invoice number">
                                <TextField size="small" fullWidth value={editForm.invoiceNumber || ''} onChange={(e) => setEditForm((f) => ({ ...f, invoiceNumber: e.target.value }))} />
                            </Field>
                            <Field label="Invoice file" hint="PDF or image">
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                    <Button component="label" variant="outlined" size="small" disabled={editUploading} sx={{ textTransform: 'none', borderColor: '#512DA8', color: '#512DA8', height: 40, whiteSpace: 'nowrap' }}>
                                        {editUploading ? 'Uploading…' : 'Upload invoice'}
                                        <input hidden type="file" accept=".pdf,.png,.jpg,.jpeg" onChange={handleEditUpload} />
                                    </Button>
                                    {editForm.invoiceUri && (
                                        <a href={editForm.invoiceUri} target="_blank" rel="noopener noreferrer" style={{ color: '#512DA8', fontSize: 13 }}>
                                            {editForm.invoiceName || 'View current'}
                                        </a>
                                    )}
                                </Box>
                            </Field>
                        </Box>
                        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mt: 2 }}>
                            <Field label="Courier">
                                <TextField size="small" fullWidth placeholder="e.g. Blue Dart, DTDC" value={editForm.courierName || ''} onChange={(e) => setEditForm((f) => ({ ...f, courierName: e.target.value }))} />
                            </Field>
                            <Field label="Tracking / AWB number">
                                <TextField size="small" fullWidth value={editForm.trackingNumber || ''} onChange={(e) => setEditForm((f) => ({ ...f, trackingNumber: e.target.value }))} />
                            </Field>
                        </Box>
                        <Box sx={{ mt: 2 }}>
                            <Field label="Remarks" hint="optional">
                                <TextField size="small" fullWidth value={editForm.remarks || ''} onChange={(e) => setEditForm((f) => ({ ...f, remarks: e.target.value }))} />
                            </Field>
                        </Box>
                    </DialogContent>
                    <DialogActions sx={{ p: 2 }}>
                        <Button onClick={() => setEditing(null)} disabled={editSaving} sx={{ textTransform: 'none', color: '#512DA8' }}>Cancel</Button>
                        <Button variant="contained" onClick={saveEdit} disabled={editSaving || editUploading} sx={{ textTransform: 'none', background: '#512DA8', '&:hover': { background: '#4527a0' } }}>
                            {editSaving ? 'Saving…' : 'Save'}
                        </Button>
                    </DialogActions>
                </Dialog>

                <DeleteModal
                    isOpen={Boolean(deleting)}
                    onClose={() => setDeleting(null)}
                    onDelete={confirmDelete}
                    loading={deleteLoading}
                    requirePassword={false}
                    title="Remove this dispatch?"
                    description={`Invoice ${deleting?.invoiceNumber || ''} will be removed and the dispatched quantities will become pending again.`}
                />
            </Grid>
        </div>
    );
};

export default OrderDetailsProds;
